// src/pages/markets/board/state/boardGroups.ts
//
// The pure model for a trader group's presentation: what it is called, what it
// says, what colour it carries, which seed column it inherits, and whether the
// picker offers it. Firestore holds the values — see useBoardGroups for the
// storage side. This file holds only the rules for reading them.
//
// The rule that motivated the whole change: a group key is NEVER a user-visible
// fallback. A group with no document reads as `humanize(key)` — possibly wrong,
// but never an internal identifier. That is what keeps `insider_gomez` out of
// the product whether or not anyone has curated it.

export const BOARD_GROUPS_VERSION = 1;

// The two conviction columns a group can inherit its presentation from. A group
// whose document does not say reads as `all_traders`, which is what an unknown
// group did before this was configurable.
export type SeedColumn = 'insider' | 'all_traders';

const SEED_COLUMNS: readonly string[] = ['insider', 'all_traders'];

export const DEFAULT_SEED_COLUMN: SeedColumn = 'all_traders';

export const PUBLIC_GROUPS: Readonly<Record<string, SeedColumn>> = Object.freeze({
    informed: 'insider',
    whales: 'all_traders',
});

export function isPublicGroup(group: string): boolean {
    return Object.prototype.hasOwnProperty.call(PUBLIC_GROUPS, group);
}

// What Firestore stores. Every field is optional on purpose: a document written
// by an older build, or one an admin has only half filled in, must still
// resolve rather than render `undefined`.
export interface BoardGroupDoc {
    label?: string;
    one_liner?: string;
    description?: string;
    color?: string;
    seed_column?: string;
    visible?: boolean;
    updated_at?: string;
    updated_by?: string;
}

// What the UI reads. Every field present, so no consumer needs a fallback of
// its own — the fallbacks all live here.
export interface BoardGroup {
    group: string;
    label: string;
    oneLiner: string;
    description: string;
    color: string;
    seedColumn: SeedColumn;
    visible: boolean;
    // False when nothing has been written for this group. The editor uses it to
    // tell "an admin wrote an empty description" from "nobody has written one".
    curated: boolean;
}

// Chosen to stay distinguishable at the 8px dot the picker and card badges draw
// them at, and to survive both themes.
export const GROUP_PALETTE: readonly string[] = Object.freeze([
    '#f59e0b',
    '#3b82f6',
    '#a78bfa',
    '#34d399',
    '#f472b6',
    '#22d3ee',
    '#fbbf24',
    '#60a5fa',
    '#2dd4bf',
    '#fb7185',
    '#818cf8',
    '#5eead4',
]);

// `buzzer_sharp` -> `Buzzer sharp`. Deliberately the same transformation the
// indexer applied, so a group nobody has curated reads exactly as it did before
// the text moved to Firestore.
export function humanize(key: string): string {
    const text = key.replace(/_/g, ' ').trim();
    return text ? text[0].toUpperCase() + text.slice(1) : key;
}

// Hashed rather than indexed by position in the schema list. The list is
// `ORDER BY group_name`, so a new group admitted upstream would shift every
// colour after it if position decided; hashing keeps a group's colour stable
// for its lifetime, with no colour stored for the 16 groups nobody curates.
export function paletteColor(key: string): string {
    let hash = 0;
    for (let i = 0; i < key.length; i++) {
        hash = (hash * 31 + key.charCodeAt(i)) | 0;
    }
    return GROUP_PALETTE[Math.abs(hash) % GROUP_PALETTE.length];
}

function seedColumnOf(value: string | undefined, group: string): SeedColumn {
    if (value && SEED_COLUMNS.includes(value)) return value as SeedColumn;
    return isPublicGroup(group) ? PUBLIC_GROUPS[group] : DEFAULT_SEED_COLUMN;
}

// A field that is present but blank counts as written: an admin clearing a
// description means the description is empty, not that it reverts to a default.
function textOf(value: string | undefined, fallback: string): string {
    return typeof value === 'string' ? value : fallback;
}

export function resolveGroup(
    group: string,
    doc: BoardGroupDoc | undefined
): BoardGroup {
    return {
        group,
        // A label must never be blank — the picker row would be unclickable
        // text — so an empty string falls back the same way a missing one does.
        label: doc?.label?.trim() ? doc.label : humanize(group),
        oneLiner: textOf(doc?.one_liner, ''),
        description: textOf(doc?.description, ''),
        color: doc?.color?.trim() ? doc.color : paletteColor(group),
        seedColumn: seedColumnOf(doc?.seed_column, group),
        // Absent means not offered. This is what lets the old
        // DEFAULT_VISIBLE_GROUPS list be deleted rather than moved: a group
        // nobody has curated is simply not in the menu.
        visible: doc?.visible === true,
        curated: doc !== undefined,
    };
}

export function resolveGroups(
    groups: readonly string[],
    docs: Readonly<Record<string, BoardGroupDoc>>
): Map<string, BoardGroup> {
    const out = new Map<string, BoardGroup>();
    for (const group of groups)
        out.set(group, resolveGroup(group, docs[group]));
    // A document for a group the schema no longer lists still resolves: a saved
    // profile pointing at a retired group must keep its name, not fall back to
    // a humanized key.
    for (const group of Object.keys(docs)) {
        if (!out.has(group)) out.set(group, resolveGroup(group, docs[group]));
    }
    return out;
}

export function isGroupOffered(group: string, isAdmin: boolean): boolean {
    if (isAdmin) return true;
    return isPublicGroup(group);
}

// Labels are admin-typed now, so two groups can still end up sharing one. The
// key is shown ONLY to an admin, who is the person able to fix it; a reader
// never sees an internal identifier under any circumstance, which is the
// property the old disambiguateTitles could not offer.
export function titleFor(
    group: string,
    resolved: BoardGroup | undefined,
    collidingLabels: ReadonlySet<string>,
    isAdmin: boolean
): string {
    const label = resolved?.label ?? humanize(group);
    if (!isAdmin) return label;
    return collidingLabels.has(label) ? `${label} (${group})` : label;
}

// The labels carried by more than one of the groups on offer. Computed over
// what is actually shown, so hiding one half of a collision resolves it.
export function collidingLabels(resolved: readonly BoardGroup[]): Set<string> {
    const counts = new Map<string, number>();
    for (const g of resolved)
        counts.set(g.label, (counts.get(g.label) ?? 0) + 1);
    const out = new Set<string>();
    for (const [label, count] of counts) {
        if (count > 1) out.add(label);
    }
    return out;
}
